import React, { useEffect, useState } from 'react';
import { connect as connectR } from 'react-redux';
import { Col, Row } from 'react-bootstrap';
import Navbar from '../navigation/navbar';
import LeftSide from '../main/left-side';
import RightSide from '../main/right-side';
import Postit from '../main/postit';
import DashboardStatistics from './cards/dashboard-statistics';
import VerticalNavigation from '../navigation/vertical-navigation';
import {
    LoadContractualContact,
    LoadCreditNoteRecipient,
    PartnerContactDto,
} from '../../store/partnerContactsSlice';
import { isFinal, RequestStatus } from '../../store/network/managedRequest';
import { LoadMansions } from '../../store/mansionsSlice';
import {
    LoadContractDetails,
    LoadSettlementBillingDetails,
    SettlementBillingDetails,
} from '../../store/userDetailsSlice';
import FrequentActionCard from './frequent-action-card';
import HighlightedActions from './highlighted-actions';
import {
    ManagedPartnerDto,
    ReloadSpecificPartner,
} from '../../store/partnerManagerSlice';
import { RootState } from '../../store';
import { LegacyUser } from '../../util/useSessionStorage';
import useTranslate from '../useTranslate';
import useFeatureAccess from '../../util/useFeatureAccess';
import { PbRequest } from '../../store/pbRequest';
import { PbGenericRequest } from '../../store/pbRequestSlice';

interface Props {
    contractualContactStatus: RequestStatus;
    partnerDataStatus: RequestStatus;
    partnerManagerStatus: RequestStatus;
    userDataRequest: PbGenericRequest;
    userData?: LegacyUser;
    getMansionsRequestStatus: RequestStatus;
    contractualContact: PartnerContactDto | undefined;
    LoadContractualContact: () => void;
    LoadCreditNoteRecipient: () => void;
    LoadMansions: () => void;
    fetchBillingRequest: PbGenericRequest;
    fetchBillingData: SettlementBillingDetails | undefined;
    LoadSettlementBillingDetails: () => void;
    LoadContractDetails: () => void;
    ReloadSpecificPartner: () => void;
    selectedPartner: ManagedPartnerDto;
}

function Dashboard(props: Readonly<Props>) {
    const strings = useTranslate('Dashboard');

    const { canReadFeature } = useFeatureAccess(props.selectedPartner);
    const hasReadMansion = canReadFeature('RESIDENTIAL_MANSION');

    const [warning, setWarning] = useState<string>(null);
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);

    useEffect(() => {
        props.LoadSettlementBillingDetails();
        props.LoadContractDetails();
        props.LoadContractualContact();
        props.LoadCreditNoteRecipient();
        props.LoadMansions();
        props.ReloadSpecificPartner();
    }, []);

    useEffect(() => {
        if (!props.fetchBillingData && props.contractualContact == null) {
            setWarning(strings.Postit3.text1);
        } else if (!props.fetchBillingData) {
            setWarning(strings.Postit3.text2);
        } else if (props.contractualContact == null) {
            setWarning(strings.Postit3.text3);
        } else {
            setWarning(null);
        }
    }, [props.fetchBillingData, props.contractualContact]);

    useEffect(() => {
        if (
            !dataLoaded &&
            isFinal(props.contractualContactStatus) &&
            isFinal(props.getMansionsRequestStatus) &&
            isFinal(props.fetchBillingRequest.status) &&
            isFinal(props.userDataRequest.status) &&
            isFinal(props.partnerDataStatus) &&
            isFinal(props.partnerManagerStatus) &&
            !!props.userData
        ) {
            setDataLoaded(true);
        }
    }, [
        dataLoaded,
        props.contractualContactStatus,
        props.getMansionsRequestStatus,
        props.fetchBillingRequest.status,
        props.userDataRequest.status,
        props.partnerDataStatus,
        props.partnerManagerStatus,
        props.userData,
    ]);

    if (dataLoaded) {
        return (
            <Navbar nav>
                <Row>
                    <LeftSide addClass="">
                        <VerticalNavigation selected="dashboard" />
                    </LeftSide>
                    <Col
                        xs={6}
                        sm={6}
                        md={6}
                        lg={6}
                        data-testid="dashboard-content"
                    >
                        <Row>
                            <HighlightedActions
                                selectedPartner={props.selectedPartner}
                            />
                        </Row>
                        {hasReadMansion && (
                            <Row>
                                <DashboardStatistics />
                            </Row>
                        )}
                        <Row>
                            <FrequentActionCard
                                selectedPartner={props.selectedPartner}
                            />
                        </Row>
                    </Col>
                    <RightSide addClass="">
                        {warning && (
                            <Postit
                                title={strings.Postit3.title}
                                link="/profile"
                            >
                                <p>{warning}</p>
                            </Postit>
                        )}
                    </RightSide>
                </Row>
            </Navbar>
        );
    } else {
        return <div />;
    }
}

export default connectR(
    (store: RootState) => ({
        contractualContactStatus:
            store.pbRequest[PbRequest.GET_CONTRACTUAL_CONTACT].status,
        contractualContact: store.partnerContacts.contractualContract,
        getMansionsRequestStatus:
            store.pbRequest[PbRequest.LOAD_MANSIONS].status,
        fetchBillingRequest:
            store.pbRequest[PbRequest.FETCH_SETTLEMENT_BILLING_DETAILS],
        fetchBillingData: store.userDetails.settlementBillingDetails,
        userDataRequest: store.pbRequest[PbRequest.GET_USER_DATA],
        userData: store.user.legacyUser,
        partnerDataStatus:
            store.pbRequest[PbRequest.GET_SPECIFIC_PARTNER].status,
        partnerManagerStatus:
            store.pbRequest[PbRequest.GET_PARTNER_MANAGER_DETAILS].status,
        selectedPartner: store.partnerManager.selectedPartner,
    }),
    {
        LoadContractualContact,
        LoadCreditNoteRecipient,
        LoadMansions,
        LoadSettlementBillingDetails,
        LoadContractDetails,
        ReloadSpecificPartner,
    }
)(Dashboard);
